import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { selectedCompanyHasProfile } from "../utils/activityProfiles";
import useIsMobile from "../hooks/useIsMobile";
import {
  CoffeeOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  QrcodeOutlined,
  RollbackOutlined,
  ShoppingOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import PermissionTabsLayout, {
  PermissionTabsRedirect,
} from "../components/PermissionTabsLayout.jsx";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";

const SALES_TABS = [
  {
    key: "commandes-restaurant",
    label: "Prise de commande",
    icon: <CoffeeOutlined />,
    path: "/gestion-ventes/commandes-restaurant",
    permission: "Gestion Commerciale.Restauration.CommandesSalle.view",
  },
  {
    key: "menu-digital",
    label: "Menu digital",
    icon: <QrcodeOutlined />,
    path: "/gestion-ventes/menu-digital",
    permission: "Gestion Commerciale.Restauration.MenuDigital.view",
  },
  {
    key: "ventes",
    label: "Ventes",
    icon: <ShoppingOutlined />,
    path: "/gestion-ventes/ventes",
    permission: "Gestion Commerciale.Ventes.Ventes.view",
  },
  {
    key: "retour-vente",
    label: "Retour de vente",
    icon: <RollbackOutlined />,
    path: "/gestion-ventes/retour-vente",
    permission: "Gestion Commerciale.Ventes.RetourVente.view",
  },
  {
    key: "livraisons",
    label: "Livraisons",
    icon: <TruckOutlined />,
    path: "/gestion-ventes/livraisons",
    permission: "Gestion Commerciale.Ventes.Livraisons.view",
  },
  {
    key: "proforma",
    label: "Proforma/Devis",
    icon: <FileTextOutlined />,
    path: "/gestion-ventes/proforma",
    permission: "Gestion Commerciale.Ventes.ProformaDevis.view",
  },
  {
    key: "paiements-entrants",
    label: "Paiements entrants",
    icon: <CreditCardOutlined />,
    path: "/gestion-ventes/paiements-entrants",
    permission: "Gestion Commerciale.Ventes.PaiementsEntrants.view",
  },
];

// Exporté pour alimenter la feuille « Aller à… » de l'écran Ventes mobile.
export const getSalesTabsForUser = (user, selectedCompany) =>
  SALES_TABS.filter((tab) => {
    if (!["commandes-restaurant", "menu-digital"].includes(tab.key)) {
      return true;
    }

    return selectedCompanyHasProfile(user, selectedCompany, "restaurant");
  });

// ⚠️ À garder synchronisée avec `isMobileVentesRoute` dans src/App.js.
const MOBILE_SHELL_ROUTES =
  /^\/gestion-ventes\/(ventes|paiements-entrants|proforma|livraisons|retour-vente)/;

export const SalesTabsRedirect = () => {
  const { user } = useAuth();
  const { selectedCompany } = useSelection();
  const tabs = React.useMemo(
    () => getSalesTabsForUser(user, selectedCompany),
    [selectedCompany, user]
  );

  return <PermissionTabsRedirect tabs={tabs} />;
};

const SalesTabsLayout = () => {
  const { user } = useAuth();
  const { selectedCompany } = useSelection();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const tabs = React.useMemo(
    () => getSalesTabsForUser(user, selectedCompany),
    [selectedCompany, user]
  );

  // Sur mobile, cinq des sept onglets sont rendus par des écrans .epm déjà hauts de
  // 100dvh − tabbar : la barre d'onglets AntD les pousserait hors du viewport. Chacun
  // fournit sa propre navigation (feuille « Aller à… »). Les deux onglets Restauration
  // restent des pages desktop longues et gardent leurs onglets et leur défilement.
  if (isMobile && MOBILE_SHELL_ROUTES.test(pathname)) {
    return <Outlet />;
  }

  return <PermissionTabsLayout tabs={tabs} />;
};

export default SalesTabsLayout;
